<script>
import axios from '@/utils/axios';
import { useToast } from 'vue-toastification';

export default {
  inject: ['setLoading'],
  data() {
    return {
      title: "Finance & Accounts",
      text: "Finance & Accounts", 
      text1: "Import CSV",
      toast: useToast(),
      selectedFile: null,
      dragActive: false,
      uploading: false,
      imports: [],
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0
      },
      import_type: 'zoho_payments',
      loadingImports: false
    }
  },

  mounted(){
   this.fetchImports();
  },

  methods: {

   formatFieldName(fieldName) {
    const fieldMap = {
      'customer_name': 'Customer Name',
      'customer_mobile': 'Mobile Number',
      'invoice_date': 'Invoice Date',
      'invoice_number': 'Invoice Number',
      'balance': 'Balance Amount'
    };
    return fieldMap[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
   },

   // Download errors as CSV
   downloadErrors(importItem) {
    const headers = ['Row', 'Field', 'Error', 'Invoice Date', 'Invoice Number', 'Customer Name', 'Mobile', 'Balance'];
    const csvRows = importItem.errors.map(error => {
     const rowData = [
      error.row,
      '',
      '',
      error.data.invoice_date || '',
      error.data.invoice_number || '',
      error.data.customer_name || '',
      error.data.customer_mobile || '',
      error.data.balance || ''
     ];
      
      // Flatten field errors
      const fieldRows = [];
      Object.entries(error.errors).forEach(([field, messages]) => {
        messages.forEach(message => {
          const flatRow = ['', error.row, field, message, ...rowData.slice(3)];
          fieldRows.push(flatRow);
        });
      });
      
      return fieldRows;
     }).flat();

     const csvContent = [headers, ...csvRows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

     const blob = new Blob([csvContent], { type: 'text/csv' });
     const url = window.URL.createObjectURL(blob);
     const link = document.createElement('a');
     link.href = url;
     link.download = `errors-${importItem.file_name.replace('.csv', '')}-${importItem.import_id}.csv`;
     link.click();
     window.URL.revokeObjectURL(url);
    }, 

   // Handle file selection via input
   handleFileSelect(event) {
    const file = event.target.files[0];
    this.validateAndSetFile(file);
   },
    
    // Handle drag and drop events
   handleDragOver(event) {
    event.preventDefault();
    this.dragActive = true;
   },
    
   handleDragLeave(event) {
    event.preventDefault();
    this.dragActive = false;
   },
    
   handleDrop(event) {
    event.preventDefault();
    this.dragActive = false;  
    const files = event.dataTransfer.files;
    if(files.length > 0){
     this.validateAndSetFile(files[0]);
    }
   },
    
   // Validate file type and size
   validateAndSetFile(file){
    if(!file) return;
    // Check if it's a CSV file
    if(file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')){
     this.$swalToast('Please select a valid CSV file', 'error');
     return;
    }
      
    // Check file size (max 5MB)
    if(file.size > 5 * 1024 * 1024){
     this.$swalToast('File size should be less than 5MB', 'error');
     return;
    }  
     this.selectedFile = file;
   },
    
   // Upload file to API
   async uploadFile() {
    if(!this.selectedFile){
     this.$swalToast('Please select a CSV file first', 'error');
     return;
    }
      
    this.uploading = true;
    this.setLoading(true);
      
    try{
     // Create FormData object
     const formData = new FormData();
     formData.append('file', this.selectedFile);
     formData.append('import_type', this.import_type);
        
     const response = await axios.post('/csv/import', formData, {
       headers: {
        'Content-Type': 'multipart/form-data'
       }
     });
        
     // Handle success
     this.$swalToast(response.data.message || 'CSV imported successfully!');
     this.selectedFile = null;
        
     // Reset file input
     this.$refs.fileInput.value = '';
     this.fetchImports();
    }catch(error){
     if(error.type === 'validation') {
      this.errors = error.errors;
     }else{
      this.$swalToast('Failed to import CSV file. Please try again.', 'error');
     }
    }finally{
     this.uploading = false;
     this.setLoading(false);
    }
   },
    
    // Remove selected file
   removeFile() {
    this.selectedFile = null;
    this.$refs.fileInput.value = '';
   },

   // Fetch import history
    async fetchImports(page = 1) {
     this.loadingImports = true; 
     try{
      const params = {
       page,
       import_type: this.import_type,
       per_page: this.pagination.per_page 
      };  
      const query_string = new URLSearchParams(params).toString();
      const response = await axios.get(`/csv/imports?${query_string}`);
        
      this.imports = response.data.data.data;
      this.pagination = {
       current_page: response.data.data.current_page,
       last_page: response.data.data.last_page,
       per_page: response.data.data.per_page,
       total: response.data.data.total
      };
     }catch(error){
      this.$swalToast(error.message, 'error');
     }finally{
      this.loadingImports = false;
     }
    },

    // Get status badge class
    getStatusClass(status){
     const statusClasses = {
       'queued': 'bg-warning',
       'processing': 'bg-info',
       'completed': 'bg-success',
       'failed': 'bg-danger'
     };
     return statusClasses[status] || 'bg-secondary';
    },
    
    // Get progress bar class
    getProgressClass(status){
     if (status === 'completed') return 'bg-success';
     if (status === 'failed') return 'bg-danger';
     if (status === 'processing') return 'bg-info';
     return 'bg-warning';
    },
    
    // Change page
    changePage(page){
     if(page >= 1 && page <= this.pagination.last_page && page !== this.pagination.current_page){
      this.fetchImports(page);
     }
    },

    downloadSample(){
     try{
      // Create download link
      const link = document.createElement('a');
      link.href = '/samples/import-zoho-payment.csv';
      link.download = 'zoho-payment-import-sample.csv';
      link.target = '_blank';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
     }catch(error){
      console.error('Download error:', error);
      this.$swalToast('Failed to download sample CSV', 'error');
     }
  }

  }
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>
  
  <!-- Page Wrapper -->
  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="">
          <router-link :to="{name: 'zoho-payments'}" class="btn btn-primary btn-sm">
            <i class="ti ti-chevrons-left"></i> Zoho Payments
          </router-link>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <div class="card" style="background-color:#7367f01f;">
       <div class="card-body pb-1">
         <div class="mb-3">
          <p>Import your existing Zoho payment records and manage them seamlessly in the system. 
            To get started, download the 
           <button type="button" class="fw-bold btn btn-primary btn-sm" @click="downloadSample">
            Zoho Payment Import Template
           </button>, 
           update it with the required payment details, and upload the CSV file. 
           Please review the 
          </p>

          </div>   
        </div>
      </div>

      <div class="card">
        <div class="card-header">
          <h5 class="card-title">CSV File Upload</h5>
        </div>
        <div class="card-body">
        <p class="text-muted">Upload your CSV file according to given format.</p>
          
          <!-- File Drop Zone -->
        <div class="dropzone-area" :class="{ 'drag-active': dragActive }" @dragover="handleDragOver" @dragleave="handleDragLeave" @drop="handleDrop" @click="$refs.fileInput.click()">
        <input type="file" ref="fileInput" accept=".csv" style="display: none;" @change="handleFileSelect"/>
            
        <div class="dz-message text-center py-5">
        <i class="ti ti-cloud-upload h1 text-muted"></i>
        <h3 v-if="!selectedFile">Drop CSV file here or click to upload</h3>
        <div v-else class="selected-file-info">
        <i class="ti ti-file-text h2 text-success"></i>
        <h5 class="text-success mt-2">{{ selectedFile.name }}</h5>
        <p class="text-muted">{{ (selectedFile.size / 1024).toFixed(2) }} KB</p>
        <button type="button" class="btn btn-sm btn-outline-danger mt-2" @click.stop="removeFile">
         <i class="ti ti-trash"></i> Remove</button>
        </div>
        </div>
        </div>
          
        <!-- Upload Button -->
        <div class="text-center mt-4" v-if="selectedFile">
         <button type="button" class="btn btn-success btn-lg" :disabled="uploading" @click="uploadFile">
          <span v-if="uploading">
           <i class="spinner-border spinner-border-sm me-2"></i>Importing...
          </span>
          <span v-else>
           <i class="ti ti-upload me-2"></i>Import CSV
           </span>
          </button>
         </div>
        </div>
       </div>

     <!-- Import History -->
      <div class="card">
       <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="card-title mb-0">Import History</h5>
        <button type="button" class="btn btn-outline-primary btn-sm" @click="fetchImports()" :disabled="loadingImports">
        <i class="ti ti-refresh" :class="{ 'spin': loadingImports }"></i> Refresh
        </button>
        </div>
        <div class="card-body">
          
        <!-- Loading State -->
        <div v-if="loadingImports" class="text-center py-4">
         <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
         </div>
         <p class="mt-2 text-muted">Loading import history...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="imports.length === 0" class="text-center py-5">
         <i class="ti ti-file-import h1 text-muted"></i>
         <h5 class="text-muted">No imports found</h5>
         <p class="text-muted">Upload your first CSV file to see import history</p>
        </div>

        <!-- Import History Table -->
        <div v-else class="table-responsive">
         <table class="table table-hover">
          <thead class="table-light">
           <tr>
            <th>File Name</th>
            <th>Status</th>
            <th>Progress</th>
            <th>Rows</th>
            <th>Success/Failed</th>
            <th>Started</th>
            <th>Completed</th>
            <th>Actions</th>
           </tr>
           </thead>
           <tbody>
            <tr v-for="importItem in imports" :key="importItem.id">
            <td>
             <div class="d-flex align-items-center">
              <i class="ti ti-file-csv me-2 text-success"></i>
             <div>
             <div class="fw-medium">{{ importItem.file_name }}</div>
             <small class="text-muted">ID: {{ importItem.import_id.substring(0, 8) }}...</small>
             </div>
             </div>
             </td>
             <td>
              <span class="badge" :class="getStatusClass(importItem.status)"> {{ importItem.status }} </span>
             </td>
             <td>
             <div class="progress" style="height: 8px; width: 80px;">
              <div class="progress-bar" :class="getProgressClass(importItem.status)" :style="{ width: importItem.progress_percentage + '%' }"></div>
             </div>
             <small class="text-muted">{{ importItem.progress_percentage }}%</small>
             </td>
             <td>
              <div class="text-center">
               <div class="fw-medium">{{ importItem.total_rows }}</div>
               <small class="text-muted">Total</small>
              </div>
             </td>
             <td>
             <div class="d-flex gap-2">
              <span class="badge bg-success-light text-success" v-if="importItem.successful_rows > 0">
               {{ importItem.successful_rows }} ✓
              </span>
              <span class="badge bg-danger-light text-danger" v-if="importItem.failed_rows > 0">
               {{ importItem.failed_rows }} ✗
              </span>
             </div>
             </td>
             <td>
              <small>{{ importItem.started_at ? $formatDate(importItem.started_at) : '' }}</small>
             </td>
             <td>
              <small>{{ importItem.completed_at ? $formatDate(importItem.completed_at) : '' }}</small>
             </td>
             <td>
              <div class="d-flex gap-1">
              <!-- View Errors Button -->
              <button v-if="importItem.errors && importItem.errors.length > 0" type="button" class="btn btn-outline-danger btn-sm" data-bs-toggle="modal" :data-bs-target="`#errorsModal${importItem.id}`">
                <i class="ti ti-alert-circle"></i>
              </button>
              </div>
             </td>
            </tr>
           </tbody>
           </table>
          </div>

          <!-- Pagination -->
          <div v-if="pagination.last_page > 1" class="d-flex justify-content-between align-items-center mt-4">
            <div class="text-muted">
              Showing {{ ((pagination.current_page - 1) * pagination.per_page) + 1 }} to 
              {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }} of 
              {{ pagination.total }} entries
            </div>

           <nav aria-label="Import history pagination">
            <ul class="pagination pagination-sm mb-0">
             <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
              <button class="page-link" @click="changePage(pagination.current_page - 1)" :disabled="pagination.current_page === 1">Previous</button>
             </li>
                
             <li v-for="page in Math.min(pagination.last_page, 5)" :key="page" class="page-item" :class="{ active: page === pagination.current_page }">
              <button class="page-link" @click="changePage(page)">{{ page }}</button>
             </li>
                
             <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
              <button class="page-link" @click="changePage(pagination.current_page + 1)" :disabled="pagination.current_page === pagination.last_page">Next
              </button>
             </li>
             </ul>
            </nav>

          </div>
          </div>
          </div>

      <!-- Error Modals -->
      <div v-for="importItem in imports" :key="`modal-${importItem.id}`" class="modal fade" 
      :id="`errorsModal${importItem.id}`" tabindex="-1">
      <div class="modal-dialog modal-xl">
      <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Import Errors - {{ importItem.file_name }}</h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
      </div>
      <div class="modal-body">
        <div v-if="importItem.errors && importItem.errors.length > 0">
          <div v-for="(error, index) in importItem.errors" :key="index" class="mb-4 p-3 border rounded bg-light">
            <div class="fw-bold text-danger mb-2">Row {{ error.row }}:</div>
            
            <!-- Field-specific errors -->
            <div v-for="(fieldErrors, fieldName) in error.errors" :key="fieldName" class="mb-2">
              <div class="fw-medium text-dark">{{ formatFieldName(fieldName) }}:</div>
              <div class="small text-danger">
                <span v-for="(message, msgIndex) in fieldErrors" :key="msgIndex" class="d-block">{{ message }}</span>
              </div>
            </div>
            
            <!-- Raw row data (collapsible) -->
            <details class="mt-3">
              <summary class="text-muted small cursor-pointer">View Raw Row Data</summary>
              <pre class="mt-2 small bg-white p-3 rounded border">{{ JSON.stringify(error.data, null, 2) }}</pre>
            </details>
          </div>
        </div>
        <div v-else class="text-center text-muted py-4">
          <i class="ti ti-check-circle h1 text-success"></i>
          <p>No errors found in this import.</p>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        <!-- Download errors CSV -->
        <button v-if="importItem.errors && importItem.errors.length > 0" @click="downloadErrors(importItem)"  class="btn btn-outline-danger">
          <i class="ti ti-download me-1"></i> Download Errors CSV
        </button>
      </div>
      </div>
      </div>
      </div>

    </div>

    <app-footer></app-footer>
  </div>
  <!-- /Page Wrapper -->
</template>

<style scoped>
.dropzone-area {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fafafa;
}

.dropzone-area:hover {
  border-color: #007bff;
  background: #f8f9ff;
}

.dropzone-area.drag-active {
  border-color: #28a745;
  background: #f8fff9;
  transform: scale(1.02);
}

.selected-file-info {
  padding: 20px;
}

.dz-message h3 {
  color: #6c757d;
  font-weight: 300;
}
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.cursor-pointer {
  cursor: pointer;
}

.bg-success-light {
  background-color: #d4edda !important;
}

.bg-danger-light {
  background-color: #f8d7da !important;
}
</style>
